import {useEffect, useMemo} from 'react'
import startCase from 'lodash/startCase.js'

import {onlyIfAutoForm, updateForm, useForm} from '../ztore.js'
import noProductImageURL from '../../images/no-product-img-01.gif'
import Currency from '../common/components/Currency.js'
import Quantity from '../common/components/Quantity.js'
import className from '../common/className.js'
import {FormattedRealDate} from '../common/components/FormattedDate.js'
import {formatDateTime, formatWeekday} from '../common/date.js'
import {isPresent} from '../common/utils.js'
import {PITNEY, UPS} from '../common/constants/ShipperNames.js'
import delay from '../common/delay.js'
import {getPackingListLogoURL} from '../data/packingLists.js'
import Icon from '../common/icons/Icon.js'

const TRACKING = 'tracking'
const DEMO = 'demo'

const CACHE_BUCKET = window.location.host.match(/\.ordoro\.com$/)
  ? 'ordoro-abode-cache-public'
  : 'ordoro-dev-abode-cache-public'

const MARYANN_URL = window.location.host.match(/\.ordoro\.com$/)
  ? 'https://maryann.gcp.prod.ordoro.com'
  : 'https://maryann.gcp.dev.ordoro.com'

const CAN_HAVE_UPDATES = [UPS, PITNEY]

function setupTrackingForm() {
  const initialForm = {
    isLoading: true,
    tracking_hash: window.location.pathname.split('/').pop(),
    company: {},
    packing_list: {branded_tracking: {}},
    order_number: '',
    tracking: {},
    lines: [],
    error: null,
  }
  return {formName: TRACKING, initialForm}
}

function updateTrackingForm(updates, meta) {
  updateForm(TRACKING, updates, meta)
}

function makeDemoBackground(rotationDegrees = 0) {
  const text = 'PREVIEW'
  const canvas = document.createElement('canvas')
  const fontSize = 24
  const canvasWidth = 108
  const canvasHeight = fontSize * 3
  canvas.setAttribute('width', canvasWidth)
  canvas.setAttribute('height', canvasHeight)

  const context = canvas.getContext('2d')
  context.fillStyle = 'rgba(0,0,0,0.05)'
  context.font = fontSize + 'px sans-serif'

  // Convert degrees to radians
  const angle = (rotationDegrees * Math.PI) / 180

  // Move the origin to the center of the canvas
  context.translate(canvasWidth / 2, canvasHeight / 2)
  // Rotate the canvas
  context.rotate(angle)
  // Draw the text centered at the origin
  context.fillText(text, -context.measureText(text).width / 2, fontSize / 2.5)

  document.body.style.backgroundImage =
    'url(' + canvas.toDataURL('image/png') + ')'
}

async function fetchTracking(tracking_hash) {
  if (tracking_hash === DEMO) {
    try {
      makeDemoBackground(-35)

      const query = new URLSearchParams(window.location.search)

      await delay(1000)

      const payload = JSON.parse(query.get('_p'))

      updateTrackingForm({
        ...payload,
        isLoading: false,
      })
    } catch (err) {
      updateTrackingForm({
        error: err.message || err.error_message,
        isLoading: false,
      })
    }

    return
  }

  try {
    const res = await fetch(
      `https://storage.googleapis.com/${CACHE_BUCKET}/tracking/${tracking_hash}/tracking.json`,
    )

    if (!res.ok) {
      throw new Error('Unable to find tracking')
    }

    const json = await res.json()

    let tracking = json.tracking

    if (CAN_HAVE_UPDATES.includes(tracking.vendor)) {
      const trackingRes = await fetch(
        `${MARYANN_URL}/tracking/${json.tracking.vendor}/${json.tracking.tracking_number}`,
      )

      if (trackingRes.ok) {
        const trackingStatusPayload = await trackingRes.json()

        tracking = {
          ...json.tracking,
          status: trackingStatusPayload.status,
          carrier_updated_date: trackingStatusPayload.updated_date,
          estimated_delivery_date:
            trackingStatusPayload.estimated_delivery ||
            json.tracking.estimated_delivery_date,
        }
      }
    }

    updateTrackingForm({
      ...json,
      tracking,
      isLoading: false,
    })
  } catch (err) {
    updateTrackingForm({
      error: err.message || err.error_message,
      isLoading: false,
    })
  }
}

function getTrackingMessage(status) {
  if (status === 'manifest') {
    return 'Label Created'
  }

  return startCase(status)
}

function OrderItem({line, showImages}) {
  const form = useForm(TRACKING)

  return (
    <li className="list__item list__items--shipment-line flex align-items-center">
      {!showImages ? null : line.product_image_url ? (
        <div className="wrap--lg-img-popover margin-right-15">
          <img
            className="img--product-thumb"
            src={line.product_image_url}
            alt={line.product_name}
          />
        </div>
      ) : (
        <div className="wrap--lg-img-popover margin-right-15">
          <img
            className="img--product-thumb"
            src={noProductImageURL}
            alt="No img"
          />
        </div>
      )}
      <div>
        <div className="fs-02 lh-sm-md">
          <strong>{line.product_name}</strong>
        </div>
        {isPresent(line.total_price) && (
          <div className="fs-01 lh-md">
            <Currency
              value={line.total_price}
              currencySymbol={form.company.currency_symbol}
            />
          </div>
        )}
        <div className="fs-01 lh-md">
          <span>Qty:</span> <Quantity value={line.quantity} />
        </div>
      </div>
    </li>
  )
}

function Tracking() {
  const {
    isLoading,
    tracking_hash,
    company,
    packing_list: {branded_tracking, ...packing_list},
    order_number,
    tracking,
    lines,
    error,
  } = useForm(TRACKING)

  useEffect(() => {
    document.title = 'Tracking Details'
  }, [])

  useEffect(() => {
    fetchTracking(tracking_hash)
  }, [tracking_hash])

  const showImages = useMemo(
    () => lines.reduce((prev, line) => prev || line.product_image_url, false),
    [lines],
  )

  const totalItems = useMemo(
    () => lines.reduce((prev, line) => prev + line.quantity, 0),
    [lines],
  )

  const logoURL = useMemo(
    () =>
      getPackingListLogoURL(
        {
          logo_hash: branded_tracking.logo_hash || packing_list.logo_hash,
          _linklogo: packing_list._linklogo,
        },
        tracking.company_id,
      ),
    [packing_list],
  )

  if (isLoading) {
    return (
      <div>
        <div className="wrap--loading">
          <div className="loading"></div>
          <div className="text--loading">
            Loading your shipping information...
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return <div>{error}</div>
  }

  return (
    <div>
      <div className="wrap--row">
        <div className="medium-10 medium-centered columns bg--white">
          <div className="flex--justify align-items-center padding-top-25 padding-bottom-25 divider--bottom">
            <h1 className="logo margin-top-0 margin-bottom-0">
              <div className="wrap--logo-img">
                <a href={packing_list.website}>
                  <img src={logoURL} alt={company.name} />
                </a>
              </div>
            </h1>
            <h2 className="fs-03 lh-md align-right">
              <strong>
                Order #<span>{order_number}</span>
              </strong>
            </h2>
          </div>
        </div>
      </div>
      <div className="wrap--row margin-top-30 margin-bottom-30">
        <div className="medium-5 medium-centered columns">
          <div className="panel panel--tracking-page w-100">
            <div className="panel__header panel__header--tracking-page">
              <h3 className="subheader subheader--tracking-page fs-01">
                Tracking Info
              </h3>
            </div>
            <div className="padding-top-25 padding-bottom-10">
              <div className="wrap--row">
                <div className="medium-12 columns">
                  {tracking.estimated_delivery_date && (
                    <ul className="list list--no-style align-center divider--bottom padding-bottom-30 margin-bottom-25">
                      <li className="list__item fs-03 lh-sm margin-bottom-5">
                        <FormattedRealDate
                          value={tracking.estimated_delivery_date}
                          format={formatWeekday}
                        />
                      </li>
                      <li className="list__item fs-05 lh-sm margin-bottom-10">
                        <strong>
                          <FormattedRealDate
                            value={tracking.estimated_delivery_date}
                          />
                        </strong>
                      </li>
                      <li className="list__item fs-01 lh-sm margin-bottom-0">
                        <span className="op-60">(Estimated Delivery Date)</span>
                      </li>
                    </ul>
                  )}
                  <div>
                    <dl className="list margin-bottom-10 flex align-items-center">
                      <dt
                        className={className`list__title margin-bottom-0 carrier-logo--tracking-page ${tracking.vendor}`}
                      />
                      <dd className="list__item fs-01 lh-md margin-bottom-0">
                        {tracking.status && (
                          <div className="fs-01 lh-md margin-bottom-0">
                            <strong>Status:</strong>{' '}
                            {getTrackingMessage(tracking.status)}
                          </div>
                        )}
                        <div className="fs-00 lh-md">
                          <strong>Tracking Number:</strong>{' '}
                          <a
                            className="btn btn--link darker border-underline v-align-base"
                            href={tracking.tracking_url}
                          >
                            {tracking.tracking_number}
                          </a>{' '}
                          <span className="op-50">→</span>
                        </div>
                      </dd>
                    </dl>
                    {tracking.carrier_updated_date && (
                      <div className="fs-00 lh-md text--lt-grey align-center margin-top-15">
                        <em>
                          Updated{' '}
                          <FormattedRealDate
                            value={tracking.carrier_updated_date}
                            format={formatDateTime}
                          />
                        </em>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="wrap--row">
        <div className="medium-4 medium-offset-2 columns">
          <div className="panel panel--tracking-page w-100">
            <div className="panel__header panel__header--tracking-page flex--justify align-items-center">
              <h3 className="subheader subheader--tracking-page fs-01 lh-md">
                Shipment Details
              </h3>
              <span>
                <Quantity value={totalItems} /> items
              </span>
            </div>
            <div className="padding-top-20 padding-bottom-20">
              <div className="wrap--row">
                <div className="medium-12 columns">
                  <ul className="list list--tracking-shipment-deets">
                    {lines.map((line, index) => (
                      <OrderItem
                        key={index}
                        line={line}
                        showImages={showImages}
                      />
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="medium-4 columns end">
          <div className="panel panel--tracking-page w-100">
            <div className="panel__header panel__header--tracking-page">
              <h3 className="subheader subheader--tracking-page fs-01">
                Store Info
              </h3>
            </div>
            <div className="padding-top-20 padding-bottom-10">
              <div className="wrap--row">
                <div className="medium-12 columns">
                  {branded_tracking.additional_info && (
                    <p className="fs-00 lh-md margin-bottom-25">
                      {branded_tracking.additional_info}
                    </p>
                  )}
                  <ul className="list list--no-style">
                    {branded_tracking.phone && (
                      <li className="list__item flex align-items-center margin-bottom-15">
                        <Icon
                          name="phone"
                          className="fs-01 lh-0 v-align-middle text--lt-grey margin-right-15"
                        />
                        <span className="fs-01 lh-md">
                          <a
                            className="btn btn--link darker border-underline"
                            href={`tel:${branded_tracking.phone}`}
                          >
                            {branded_tracking.phone}
                          </a>
                        </span>
                      </li>
                    )}
                    <li className="list__item flex align-items-center margin-bottom-15 word-break">
                      <Icon
                        name="envelope"
                        className="fs-01 lh-0 v-align-middle text--lt-grey margin-right-15"
                      />
                      <span className="fs-01 lh-md">
                        <a
                          className="btn btn--link darker border-underline"
                          href={`mailto:${branded_tracking.email}`}
                        >
                          {branded_tracking.email}
                        </a>
                      </span>
                    </li>
                    {branded_tracking.return_policy_url && (
                      <li className="list__item flex align-items-center margin-bottom-15 word-break">
                        <Icon
                          name="return-policy"
                          className="fs-01 lh-0 v-align-middle text--lt-grey margin-right-15"
                        />
                        <span className="fs-01 lh-md">
                          <a
                            className="btn btn--link darker border-underline"
                            href={branded_tracking.return_policy_url}
                          >
                            <strong>Return Policy</strong>
                          </a>{' '}
                          <span className="op-50">→</span>
                        </span>
                      </li>
                    )}
                  </ul>
                  {branded_tracking.social_links.length > 0 && (
                    <div className="flex divider--top padding-top-15 margin-top-25">
                      <ul className="list list--no-style flex align-items-center margin-auto">
                        {branded_tracking.social_links.map((link, index) => (
                          <li
                            key={index}
                            className="list__item list__item--social-icon margin-right-10"
                          >
                            <a
                              className="btn btn--link darker no-underline"
                              href={link.url}
                              title={link.title}
                            >
                              <Icon
                                name={link.icon}
                                className="fs-01 lh-0 v-align-middle"
                              />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="wrap--row margin-top-25 padding-bottom-0">
        <div className="medium-10 medium-centered columns bg--white">
          <div className="align-center divider--top padding-top-25 padding-bottom-25">
            <p className="fs-00 lh-sm inline-block v-align-middle margin-right-5 margin-bottom-0">
              Powered by
            </p>
            <a
              className="link--logo inline-block"
              href="https://www.ordoro.com"
              target="_blank"
              rel="noreferrer"
            >
              <svg
                className="img--logo v-align-middle"
                viewBox="0 0 316.43 50.73"
              >
                <g>
                  <path
                    className="logotype"
                    d="M108.78,18.49V32.23c0,12.14-7.09,18.49-18,18.49S72.74,44.37,72.74,32.23V18.49C72.74,6.36,79.83,0,90.8,0S108.78,6.36,108.78,18.49ZM82.83,18.2V32.52c0,6.58,2.85,9.65,8,9.65s8-3.07,8-9.65V18.2c0-6.58-2.85-9.65-8-9.65S82.83,11.62,82.83,18.2Z"
                  />
                  <path
                    className="logotype"
                    d="M125.88,49.7H116V1h18.28c9.28,0,14.39,5.12,14.39,13.09v4.53c0,5.7-2.77,9.87-7.67,11.84l9.06,19.22H139.55l-8.26-18h-5.41Zm0-25.87h6.29c4.6,0,6.65-2.12,6.65-5.7V14.69c0-3.65-2-5.77-6.51-5.77h-6.43Z"
                  />
                  <path
                    className="logotype"
                    d="M173.76,1c11.54,0,17.76,6.58,17.76,17.25V32.6c0,10.74-6.22,17.1-17.76,17.1h-16.6V1ZM167,41.37h6.14c5.77,0,8.33-3.58,8.33-9v-14c0-5.49-2.63-9-8.33-9H167Z"
                  />
                  <path
                    className="logotype"
                    d="M234.13,18.49V32.23c0,12.14-7.09,18.49-18,18.49s-18.06-6.35-18.06-18.49V18.49C198.09,6.36,205.18,0,216.15,0S234.13,6.36,234.13,18.49Zm-25.95-.29V32.52c0,6.58,2.85,9.65,8,9.65s8-3.07,8-9.65V18.2c0-6.58-2.85-9.65-8-9.65S208.18,11.62,208.18,18.2Z"
                  />
                  <path
                    className="logotype"
                    d="M250.94,49.7h-9.87V1h18.28c9.28,0,14.39,5.12,14.39,13.09v4.53c0,5.7-2.77,9.87-7.67,11.84l9.06,19.22H264.61l-8.26-18h-5.41Zm0-25.87h6.29c4.6,0,6.65-2.12,6.65-5.7V14.69c0-3.65-2-5.77-6.51-5.77h-6.43Z"
                  />
                  <path
                    className="logotype"
                    d="M316.43,18.49V32.23c0,12.14-7.09,18.49-18,18.49S280.4,44.37,280.4,32.23V18.49C280.4,6.36,287.49,0,298.45,0S316.43,6.36,316.43,18.49Zm-25.95-.29V32.52c0,6.58,2.85,9.65,8,9.65s8-3.07,8-9.65V18.2c0-6.58-2.85-9.65-8-9.65S290.48,11.62,290.48,18.2Z"
                  />
                </g>
                <g>
                  <path
                    className="mark"
                    d="M57.49,25.3,43.12.4H14.37L0,25.3,14.37,50.19H43.12ZM18.12,43.7,7.5,25.3,18.12,6.89H39.37L50,25.3,39.37,43.7Z"
                  />
                  <polygon
                    className="mark"
                    points="35.32 13.9 24.29 13.9 30.87 25.3 24.29 36.69 35.32 36.69 41.9 25.3 35.32 13.9"
                  />
                </g>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default onlyIfAutoForm(Tracking, setupTrackingForm)
